import { Button, Col, Divider, Form, GetRef, Modal, Row, Space, Steps, Table, TableColumnsType, Image } from "antd";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useModelServiceGetBackendv2Model1 } from "../../backendapi/queries";
import TextArea from "antd/es/input/TextArea";
import { BomModelGeneratorEditPointDTO, BomModelGeneratorPointAssetDTO, BomModelGeneratorPointDTO, BomModelGeneratorService, BomModelGeneratorViewDTO, ModelService, ModelViewDTO } from "../../backendapi/requests";
import Input, { InputRef } from "antd/es/input/Input";
import { ConfigContext } from "../../contexts/ConfigContext";
import { GenerateODataFilter } from "../../helpers/odataFunctions";
import { FilterGroup } from "../../types/types";
import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { BASE_PATH } from "../../Environment";

const CreateBomModel: React.FC<{ modelId?: number, isOpen: boolean, onCancel: () => void, onCreated: () => void }> = ({ modelId, isOpen, onCancel, onCreated }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [state, setState] = useState<BomModelGeneratorViewDTO | undefined>(undefined);
    const [excelData, setExcelData] = useState("");
    const [selectedModel, setSelectedModel] = useState(modelId);

    const handleCancel = useCallback(() => {
        setCurrentStep(0);
        setSelectedModel(modelId);
        setState(undefined);
        setExcelData("");
        onCancel();
    }, []);

    const handleOk = useCallback(() => {
        if (currentStep == 0) {
            if (selectedModel) {
                BomModelGeneratorService.postBackendv2BommodelgeneratorGenerate({ data: excelData, modelId: selectedModel, points: [] }).then(result => {
                    setState(result);

                    if (result.variants.length > 0) {
                        setCurrentStep(2);
                    } else {
                        setCurrentStep(1);
                    }
                });
            }
        } else if (currentStep == 1) {
            if (state && state.variants.length > 0) {
                setCurrentStep(2);
            }
        } else if (currentStep == 2 && selectedModel && state && state.variants.length > 0) {
            BomModelGeneratorService.postBackendv2BommodelgeneratorCreate({
                modelId: selectedModel, variants: state.variants.map(e => ({
                    title: e.title,
                    metadata: e.metadata.reduce((ac, a) => ({ ...ac, [a.name]: a.value }), {}),
                    assets: Object.entries(e.assets).reduce((ac, a) => ({ ...ac, [a[0]]: a[1].name }), {})
                }))
            }).then(() => {
                setCurrentStep(0);
                setSelectedModel(modelId);
                setState(undefined);
                setExcelData("");
                onCreated();
            });
        }
    }, [currentStep, selectedModel, excelData, state]);

    let canContinue = false;
    if (currentStep == 0 && selectedModel) {
        canContinue = true;
    }
    if (currentStep == 1 && state && state.variants.length > 0) {
        canContinue = true;
    }
    if (currentStep == 2) {
        canContinue = true;
    }

    return <Modal open={isOpen} width='90%' onCancel={handleCancel} onOk={handleOk} okButtonProps={{ disabled: !canContinue }} okText={currentStep == 2 ? 'Create' : 'Next'}>
        {currentStep == 0 && <CreateBomModelStep1 excelData={excelData} onExcelDataChange={setExcelData} modelId={selectedModel} onModelChange={setSelectedModel} />}
        {currentStep == 1 && <CreateBomModelStep2 state={state!} onChange={setState} />}
        {currentStep == 2 && <CreateBomModelStep3 state={state!} onChange={setState} />}
        <Steps current={currentStep} items={[
            { title: 'Model', description: state?.model?.title, onClick: () => setCurrentStep(0) },
            { title: 'Configure', description: state == undefined ? '' : `${state.variants.length} variants`, onClick: selectedModel ? () => setCurrentStep(1) : undefined },
            { title: 'Data', description: '' }
        ]} />
    </Modal>
}

const CreateBomModelStep1: React.FC<{ modelId?: number, excelData: string, onModelChange: (modelId: number) => void, onExcelDataChange: (data: string) => void }> = ({ modelId, excelData, onModelChange, onExcelDataChange }) => {
    const { currentClient } = useContext(ConfigContext);
    const { data: model } = useModelServiceGetBackendv2Model1({ key: modelId! }, undefined, { enabled: !!modelId });
    const [query, setQuery] = useState("");
    const [models, setModels] = useState<ModelViewDTO[]>([]);
    const [loading, setLoading] = useState(false);

    const handleSearch = useCallback(() => {
        const filter: FilterGroup = {
            type: 'group', filters: [
                { type: 'search', values: [{ value: query }], property: 'title', name: 'title' },
                { type: 'search', values: [{ value: query }], property: 'metadata.article_number,metadata.ean', name: 'metadata' }
            ], join: 'or'
        };

        setLoading(true);
        ModelService.getBackendv2Model(currentClient.id, undefined, '10', undefined, GenerateODataFilter([filter])).then(result => {
            setModels(result.value);
        }).finally(() => {
            setLoading(false);
        })
    }, [query]);

    let template = "\"title\"";
    if (model != undefined) {
        model.placementpoints.forEach(e => template += '\t"' + e.label + '"');
        model.surfaces.forEach(e => template += '\t"' + e.label + '"');
        model.metadata.forEach(e => template += '\t"' + e.name + '"');
    }

    return <div>
        <Row gutter={16}>
            <Col span={12}>
                <h2>Model</h2>
                {model ? <div>
                    <h3>{model.title}</h3>
                </div> :
                    <div>
                        <Space.Compact style={{ width: '100%' }}>
                            <Input onPressEnter={handleSearch} type="search" value={query} onChange={e => setQuery(e.currentTarget.value)} />
                            <Button onClick={handleSearch} loading={loading}>Search</Button>
                        </Space.Compact>

                        {models.map(model => <h4 key={model.name}>
                            <Image
                                preview={false}
                                src={BASE_PATH + model.thumbnail}
                                height={50}
                            /> {model.title}
                            <Button onClick={() => onModelChange(model.id)}>Select</Button>
                        </h4>)}
                    </div>}
            </Col>
            <Col span={12}>
                <h2>Data</h2>
                {model != undefined &&
                    <CopyToClipboard text={template} options={{ format: 'text/plain' }}>
                        <Button>Copy template to clipboard</Button>
                    </CopyToClipboard>
                }
                <TextArea placeholder="paste data here" value={excelData} onChange={e => onExcelDataChange(e.currentTarget.value)} rows={6} />
            </Col>
        </Row>
    </div>
}

const CreateBomModelStep2: React.FC<{ state: BomModelGeneratorViewDTO, onChange: (state: BomModelGeneratorViewDTO) => void }> = ({ state, onChange }) => {
    const handlePointChange = useCallback((point: BomModelGeneratorEditPointDTO) => {
        let points: BomModelGeneratorEditPointDTO[] = state.points.map(p => ({ name: p.name, assets: p.assets.map(e => e.name) }));
        points = [...points.filter(e => e.name != point.name), point];

        BomModelGeneratorService.postBackendv2BommodelgeneratorGenerate({ modelId: state.model.id, points: points, data: '' }).then(result => {
            onChange(result)
        })
    }, [state]);

    return <div>
        <Row wrap gutter={16}>
            {state.points.map(point => <Col span={8} key={point.name}>
                <CreateBomModelStep2Point onChange={handlePointChange} point={point} />
            </Col>)}
        </Row>

    </div>
}

const CreateBomModelStep2Point: React.FC<{ point: BomModelGeneratorPointDTO, onChange: (point: BomModelGeneratorEditPointDTO) => void }> = ({ point, onChange }) => {
    const [name, setName] = useState("");

    const handleDelete = useCallback((name: string) => {
        onChange({ name: point.name, assets: point.assets.map(e => e.name).filter(e => e != name) });
    }, []);

    const handleAdd = useCallback(() => {
        var newAssets = name.split('\n').map(e => e.trim());
        onChange({ name: point.name, assets: [...point.assets.map(e => e.name), ...newAssets] });
        setName("");
    }, [name]);


    return <div>
        <h2>{point.title}</h2>
        <TextArea rows={1} value={name} onChange={e => setName(e.currentTarget.value)} /><Button onClick={handleAdd}>Add</Button>
        {point.assets.map(e => <h4>{e.title}<Button onClick={() => handleDelete(e.name)}>remove</Button></h4>)}
        <Divider />
    </div>
}

const CreateBomModelStep3: React.FC<{ state: BomModelGeneratorViewDTO, onChange: (state: BomModelGeneratorViewDTO) => void }> = ({ state, onChange }) => {
    const metadataCols = state.variants.length > 0 ? state.variants[0].metadata.map(e => e.name) : [];

    const dataSource = state.variants.map((variant, i) => {
        let item: Item = {
            key: i,
            title: variant.title,
            assets: variant.assets,
            metadata: {}
        };

        metadataCols.forEach(c => {
            item.metadata[c] = variant.metadata.find(e => e.name == c)?.value ?? '';
        });

        return item;
    });

    const handleSave = (row: { [key: string]: string }, index: number) => {
        const variants = [...state.variants];
        const variant = { ...variants[index] };

        if (row["title"] != undefined) {
            variant.title = row.title;
        }

        metadataCols.forEach(m => {
            if (row["metadata:" + m] != undefined) {
                variant.metadata = variant.metadata.filter(e => e.name !== m);
                variant.metadata.push({ name: m, value: row["metadata:" + m], locked: false });
            }
        });

        variants[index] = variant;
        const newState = { ...state, variants: variants }

        onChange(newState);
    };

    const columns: TableColumnsType<Item> = [
        {
            title: 'Title',
            width: 200,
            dataIndex: 'title',
            key: 'title',
            fixed: true,
            onCell: (record: Item) => ({
                record,
                editable: true,
                dataIndex: 'title',
                title: 'Title',
                handleSave,
            }),
        },
        ...state.points.map(p => ({
            title: p.title,
            width: 200,
            dataIndex: 'point:' + p.name,
            key: 'point:' + p.name,
            fixed: true,
            render: (_: any, record: Item) => (
                <>
                    {record.assets[p.name] && <Image
                        preview={false}
                        src={BASE_PATH + record.assets[p.name].thumbnail}
                        height={50}
                    />}
                    {record.assets[p.name]?.title}
                </>)
        })),
        ...metadataCols.map(m => ({
            title: m,
            width: 100,
            dataIndex: 'metadata:' + m,
            key: 'metadata:' + m,
            render: (_: any, record: Item) => record.metadata[m],
            onCell: (record: Item) => ({
                record,
                editable: true,
                dataIndex: 'metadata:' + m,
                title: m,
                handleSave,
            }),
        }))
    ];

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };

    return <div>

        <Table<Item>
            pagination={false}
            columns={columns}
            dataSource={dataSource}
            scroll={{ x: 'max-content' }}
            components={components}
        />
    </div>
}

interface Item {
    key: number;
    title: string;
    assets: { [key: string]: BomModelGeneratorPointAssetDTO };
    metadata: { [key: string]: string };
}

interface EditableRowProps {
    index: number;
}

type FormInstance<T> = GetRef<typeof Form<T>>;
const EditableContext = React.createContext<FormInstance<any> | null>(null);

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};

interface EditableCellProps {
    title: React.ReactNode;
    editable: boolean;
    dataIndex: keyof Item;
    record: Item;
    handleSave: (record: Item, index: number) => void;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef<InputRef>(null);
    const form = useContext(EditableContext)!;

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);

        if (dataIndex.startsWith("metadata:")) {
            form.setFieldsValue({ [dataIndex]: record.metadata[dataIndex.substring(9)] });
        } else {
            form.setFieldsValue({ [dataIndex]: record[dataIndex] });
        }
    };

    const save = async () => {
        try {
            const values = await form.validateFields();

            toggleEdit();
            handleSave(values, record.key);
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        }
    };

    let childNode = children;

    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{ margin: 0 }}
                name={dataIndex}
            >
                <Input ref={inputRef} onPressEnter={save} onBlur={save} />
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap"
                style={{ paddingInlineEnd: 24, minHeight: 22 }}
                onClick={toggleEdit}
            >
                {children}
            </div>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};

export default CreateBomModel;